import { i18n } from "@pathwright/ux/src/components/lng/I18nextProvider"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useMemo } from "react"
import { useShareDataContext } from "./ShareDataContextProvider"
import { CohortShareInviteRoleGatedReasonType } from "@pathwright/graphql-client/src/generated"

// NOTE: these roles are listed in order of preference as to which role should beused as defualt.
// Some cohorts (i.e. Child Cohorts of a Collection) do not permit inviting learners
// or observers directly. To work around this, let us include all roles, ordering them according to which
// role we prefer to select as default.

// Note: these keys match up with the GQL field defined here:
// https://github.com/duointeractive/pathwright/blob/bc309d718fddff65c3058e4d52eabcd22bfde550/packages/pathwright-graphql/graphql/models/group/types.js#L101
const defaultRolesConfig = {
  group_member: {
    color: "blue"
  },
  group_moderator: {
    color: "green"
  },
  group_admin: {
    color: "orange"
  },
  // Cohort viewer is never displayed, no color
  cohort_viewer: {},
  cohort_learner: {
    color: "blue"
  },
  cohort_observer: {
    color: "gray"
  },
  cohort_moderator: {
    color: "green"
  },
  cohort_teacher: {
    color: "orange"
  },
  // We don't currently make the course editor available in the ui.
  // cohort_course_editor: {
  //   color: "gray"
  // },
  school_member: {
    color: "gray"
  },
  school_admin: {
    color: "gray"
  },
  school_staff: {
    color: "gray"
  }
}

const defaultRoles = Object.keys(defaultRolesConfig)

export const getInviteRoleUrl = (invite) => invite?.url ?? null
export const getInviteRoleInt = (invite) => invite?.role ?? null
export const isInviteRoleGated = (invite) => !!invite?.gate_reason
export const isInviteRoleVisible = (invite) =>
  !!invite?.url || !!invite?.gate_reason

export function getGateReasonTooltip(invite, t) {
  if (!isInviteRoleGated(invite)) return null

  const tPrefix = "share_card.add_tab"

  switch (invite.gate_reason) {
    case CohortShareInviteRoleGatedReasonType.CohortSeatsFilled:
    case CohortShareInviteRoleGatedReasonType.CohortLicensedSeatsFilled:
      return t(
        `${tPrefix}.All learner seats are filled. Observers and staff do not count toward the seat limit.`
      )
    default:
      return null
  }
}

export function getDefaultRole(rolePermissions) {
  return defaultRoles.find((role) => rolePermissions[role]?.url)
}

export function getRolesConfig(rolePermissions) {
  const defaultRole = getDefaultRole(rolePermissions)

  return Object.entries(defaultRolesConfig).reduce((acc, [key, value]) => {
    if (isInviteRoleVisible(rolePermissions[key])) {
      const invite = rolePermissions[key]
      acc[key] = {
        ...value,
        isDefault: key === defaultRole,
        disabled: isInviteRoleGated(invite),
        url: getInviteRoleUrl(invite),
        role: getInviteRoleInt(invite),
        gate_reason: invite?.gate_reason ?? null
      }
    }
    return acc
  }, {})
}

// Merges in translated labels for each role.
export function useRolesConfigWithLabels() {
  const { permissions } = useShareDataContext()
  const { t, tc, conditionalT } = useTranslate()

  const rolesConfigWithLabels = useMemo(() => {
    let config = []
    if (permissions.invite_roles) {
      // Get base config.
      const rolesConfig = getRolesConfig(permissions.invite_roles)
      // Merge in translated labels.
      config = Object.entries(rolesConfig).reduce((acc, [key, value]) => {
        const tPrefix = `share_card.link_tab.roles.${key}`
        acc[key] = {
          ...value,
          label: tc(`${tPrefix}.label`),
          includes: conditionalT(`${tPrefix}.includes`),
          // Map over feature keys (which i18next transforms into an object with the item's index as it's key).
          features: i18n.exists(`${tPrefix}.features`)
            ? Object.keys(
                t(`${tPrefix}.features`, {
                  returnObjects: true
                }) || {}
              ).map((featureKey) => ({
                label: tc(`${tPrefix}.features.${featureKey}.label`),
                description: tc(`${tPrefix}.features.${featureKey}.description`)
              }))
            : []
        }

        return acc
      }, {})
      return config
    }
  }, [permissions])

  return rolesConfigWithLabels
}
